import {
  RULON_INSTALL_TYPES,
  RULON_FABRIC_LINES,
  RULON_SERIES,
  findRulonGroupId,
  resolveRulonNavOptions,
  seriesOptionsForType,
  showFabricStep,
  type RulonNavOptionCopy,
  type RulonSelection,
  type RulonSibling,
} from '../../lib/rulonnyeNavigator';
import {
  CFG_CHIP_GRID_3,
  CFG_CHIP_NAV_COMPACT,
  CFG_NAV_GRID_WIDE_LAST,
} from '../../lib/configuratorChip';
import { chipAccentStyle } from '../../lib/configuratorAccent';

type Props = {
  siblings: RulonSibling[];
  selection: RulonSelection;
  activeGroupId: string | null;
  onNavigate: (groupId: string) => void;
  onSelectionChange: (next: RulonSelection) => void;
  hapticSelection: () => void;
  /** Подписи карточек из админки «Тексты»; без них — встроенные дефолты. */
  navCopy?: {
    install_types?: RulonNavOptionCopy[];
    series?: RulonNavOptionCopy[];
    fabric_lines?: RulonNavOptionCopy[];
  } | null;
};

function NavSection({
  title,
  gridClass,
  children,
}: {
  title: string;
  gridClass: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-3.5">
      <p className="text-xs font-semibold mb-2 flex items-center gap-1.5" style={{ color: 'var(--text-2)' }}>
        {title}
      </p>
      <div className={gridClass}>{children}</div>
    </div>
  );
}

function OptionCard({
  label,
  hint,
  active,
  disabled,
  onClick,
}: {
  label: string;
  hint: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={CFG_CHIP_NAV_COMPACT}
      style={chipAccentStyle('protocol', active)}
    >
      <span className="text-xs font-bold leading-tight">{label}</span>
      <span
        className="text-[9px] font-normal leading-tight line-clamp-1 w-full"
        style={{ color: active ? 'inherit' : 'var(--text-3)', opacity: active ? 0.85 : 1 }}
      >
        {hint}
      </span>
    </button>
  );
}

export function RulonnyeTypeNavigator({
  siblings,
  selection,
  activeGroupId,
  onNavigate,
  onSelectionChange,
  hapticSelection,
  navCopy,
}: Props) {
  const installTypes = resolveRulonNavOptions(RULON_INSTALL_TYPES, navCopy?.install_types);
  const seriesPool = resolveRulonNavOptions(RULON_SERIES, navCopy?.series);
  const fabricLines = resolveRulonNavOptions(RULON_FABRIC_LINES, navCopy?.fabric_lines);
  const seriesOpts = seriesOptionsForType(selection.installType).map((opt) => {
    const row = seriesPool.find((s) => s.id === opt.id);
    return row ? { ...opt, hint: row.hint, label: row.label } : opt;
  });
  const fabricVisible = showFabricStep(selection);

  const tryNavigate = (next: RulonSelection) => {
    const gid = findRulonGroupId(siblings, next);
    if (gid && gid !== activeGroupId) {
      hapticSelection();
      onNavigate(gid);
    }
  };

  return (
    <div className="mt-3">
      <p className="text-[10px] font-bold uppercase tracking-widest mb-3 flex items-center gap-1.5" style={{ color: 'var(--text-3)' }}>
        <span style={{ color: 'var(--cyan)' }}>◈</span>
        Выберите тип шторы
      </p>

      <NavSection title="Тип установки" gridClass={CFG_NAV_GRID_WIDE_LAST}>
        {installTypes.map((opt) => {
          const active = selection.installType === opt.id;
          return (
            <OptionCard
              key={opt.id}
              label={opt.label}
              hint={opt.hint}
              active={active}
              onClick={() => {
                const next: RulonSelection = {
                  installType: opt.id,
                  series: opt.id === 'Открытая' || opt.id === 'В коробе' ? '38 мм' : null,
                  fabric: opt.id === 'Открытая' ? 'Классическая' : null,
                };
                onSelectionChange(next);
                tryNavigate(next);
              }}
            />
          );
        })}
      </NavSection>

      {seriesOpts.length > 0 && (
        <NavSection title="Диаметр профиля" gridClass={CFG_CHIP_GRID_3}>
          {seriesOpts.map((opt) => {
            const active = selection.series === opt.id;
            return (
              <OptionCard
                key={opt.id}
                label={opt.label}
                hint={opt.hint}
                active={active}
                onClick={() => {
                  const next: RulonSelection = {
                    installType: selection.installType,
                    series: opt.id,
                    fabric:
                      selection.installType === 'Открытая' && opt.id === '38 мм'
                        ? selection.fabric ?? 'Классическая'
                        : null,
                  };
                  onSelectionChange(next);
                  tryNavigate(next);
                }}
              />
            );
          })}
        </NavSection>
      )}

      {fabricVisible && (
        <NavSection title="Тип полотна" gridClass={CFG_NAV_GRID_WIDE_LAST}>
          {fabricLines.map((opt) => {
            const active = selection.fabric === opt.id;
            return (
              <OptionCard
                key={opt.id}
                label={opt.label}
                hint={opt.hint}
                active={active}
                onClick={() => {
                  const next: RulonSelection = {
                    installType: selection.installType,
                    series: selection.series,
                    fabric: opt.id,
                  };
                  onSelectionChange(next);
                  tryNavigate(next);
                }}
              />
            );
          })}
        </NavSection>
      )}
    </div>
  );
}
